import Badge from 'molecules/Badge';
import Breadcrumbs from 'molecules/Breadcrumbs';
import Heading from 'molecules/Heading';
import Icon from 'molecules/Icon';
import { IconIds } from 'molecules/Icon/types';
import Image from 'molecules/Image';
import Link from 'molecules/Link';
import Section from 'molecules/Section';

import ScrollFadeSection from 'components/Animations/ScrollFadeSection';

import { hasArrayValues } from 'utils/array/arrays';
import { getAuthorPath } from 'utils/authors';
import { getCleanDate } from 'utils/parse/parseDate';

import type { FC } from 'react';

interface HeroMeta {
  icon: IconIds;
  label: string;
}

export const Meta: FC<HeroMeta> = ({ icon, label }) => (
  <div className="flex items-center gap-3 text-md text-gray-300">
    <Icon icon={icon} size={22} />
    <span className="whitespace-nowrap">{label}</span>
  </div>
);

const HeroBlog: FC<HeroBlogProps> = ({
  title,
  summary,
  featuredImage,
  publishDate,
  lastUpdated,
  readTime,
  tags,
  author,
  breadcrumbTitle,
  hideGradient,
  hideTitleAndSummary,
  hideImage,
}) => {
  // Null unless this author has a published profile page, so a byline never
  // links to a 404.
  const authorPath = getAuthorPath(author?.value?.data);

  return (
    <Section darkMode hasGradientTransition={!hideGradient}>
      <div className="flex flex-col gap-8 sm:gap-12 lg:gap-14">
        <Breadcrumbs breadcrumbTitle={breadcrumbTitle} />
        <div className="flex flex-col justify-start gap-8 sm:gap-16 lg:flex-row lg:items-center lg:gap-[50px]">
          <div className="flex w-full flex-col gap-6 lg:w-1/2">
            {!hideTitleAndSummary && (
              <>
                {hasArrayValues(tags) && (
                  <ScrollFadeSection delay={0.2} animateOnMount>
                    <div className="flex flex-wrap gap-4">
                      {tags.map(tag => (
                        <Badge key={tag} label={tag} color="gray" />
                      ))}
                    </div>
                  </ScrollFadeSection>
                )}
                <Heading heading={title} body={summary} alignment="left" size="lg" as="h1" animateOnMount />
                <ScrollFadeSection delay={0.4} animateOnMount>
                  <div className="flex flex-col flex-wrap gap-3 sm:flex-row">
                    {publishDate && (
                      <Meta
                        icon="calendar"
                        label={getCleanDate(publishDate, { year: 'numeric', month: 'short', day: '2-digit' })}
                      />
                    )}
                    {lastUpdated && (
                      <Meta
                        icon="clock"
                        label={`Last Updated: ${getCleanDate(lastUpdated, { year: 'numeric', month: 'short', day: '2-digit' })}`}
                      />
                    )}
                    {readTime && <Meta icon="book-open" label={`${readTime} min read`} />}
                  </div>
                </ScrollFadeSection>
                {author?.value.data.name && (
                  <ScrollFadeSection delay={0.5} animateOnMount>
                    <div className="flex items-center gap-3">
                      {author?.value.data.avatar?.url && (
                        <div className="size-12 shrink-0 grow-0 basis-12 overflow-hidden rounded border border-primary-200">
                          <Image
                            url={author.value.data.avatar?.url}
                            alt={author.value.data.name || ''}
                            className="size-full"
                            objectCover
                            noFill={true}
                            width={64}
                            height={64}
                            sizes="64px"
                          />
                        </div>
                      )}
                      <div className="flex flex-col gap-x-4 gap-y-1 sm:flex-row sm:items-center">
                        {authorPath ? (
                          <Link
                            href={authorPath}
                            className="whitespace-nowrap text-xl font-bold text-white underline-offset-4 hover:underline"
                            enablePreview={false}
                          >
                            {author.value.data.name}
                          </Link>
                        ) : (
                          <span className="whitespace-nowrap text-xl font-bold text-white">
                            {author.value.data.name}
                          </span>
                        )}
                        {author.value.data.title && (
                          <span className="text-lg text-gray-300">{author.value.data.title}</span>
                        )}
                      </div>
                    </div>
                  </ScrollFadeSection>
                )}
              </>
            )}
          </div>
          <div className="relative w-full lg:w-1/2">
            {!hideImage && (
              <div className="overflow-hidden rounded-lg">
                <Image
                  url={featuredImage?.file?.url || featuredImage?.url}
                  className="size-full"
                  aspectRatio="16/9"
                  objectCover
                  alt={featuredImage?.alt || featuredImage?.display_name || ''}
                  priority
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </Section>
  );
};

export default HeroBlog;
